import { chain, get, has, keyBy, map, set } from 'lodash';
import ProgramBehaviourDataGroupsDB from 'src/fb/programBehaviourDataGroups-db';
import ProgramBehaviourDataItemsDB from 'src/fb/programBehaviourDataItems-db';

export default {
  setProgramBehaviourDataGroups(payload) {
    if (!payload.groups) {
      return;
    }

    this.programBehaviourDataGroupCache = payload.groups;
    this.allProgramBehaviourDataGroupsLoaded = true;
  }, // setProgramBehaviourDataGroups()

  setProgramBehaviourDataItems({ groupId, items }) {
    if (!groupId || !items) {
      return;
    }

    this.programBehaviourDataItemCache[groupId] = items;
    this.allProgramBehaviourDataItemsLoaded[groupId] = true;
  }, // setProgramBehaviourDataItems()

  addProgramBehaviourDataItemMutation({ groupId, itemId, item }) {
    if (!groupId || !itemId || !item) {
      return;
    }
    set(this.programBehaviourDataItemCache, [groupId, itemId], item);
  }, // addProgramBehaviourDataItemMutation()

  addProgramBehaviourDataGroup(payload) {
    if (!payload.group) {
      return;
    }
    set(this.programBehaviourDataGroupCache, payload.group.id, payload.group);
  }, // addProgramBehaviourDataGroup()

  clearProgramBehaviourDataCache() {
    this.programBehaviourDataGroupCache = {};
    this.programBehaviourDataItemCache = {};
    this.allProgramBehaviourDataGroupsLoaded = false;
    this.allProgramBehaviourDataItemsLoaded = {};
  }, // clearProgram()

  async loadProgramBehaviourDataGroups({ programId, loadItems = false, rejectDeleted = true }) {
    if (!programId) {
      return;
    }

    const constraints = rejectDeleted
      ? [['isDeleted', '==', false]]
      : null;

    if (!this.allProgramBehaviourDataGroupsLoaded || !rejectDeleted) {
      const programBehaviourDataGroupsDb = new ProgramBehaviourDataGroupsDB(programId);
      let groups = await programBehaviourDataGroupsDb.readAll(constraints, 'order');
      groups = keyBy(groups, 'id');
      this.setProgramBehaviourDataGroups({ groups });
    }

    if (loadItems) {
      const loadItemsActions = map(this.programBehaviourDataGroupCache, async (group) => {
        if ((get(this.allProgramBehaviourDataItemsLoaded, group.id, false) !== true) || !rejectDeleted) {
          const programBehaviourDataItemsDb = new ProgramBehaviourDataItemsDB(programId, group.id);
          let items = await programBehaviourDataItemsDb.readAll(constraints, 'order');

          items = keyBy(items, 'id');
          this.setProgramBehaviourDataItems({ groupId: group.id, items });
        }
      });
      await Promise.all(loadItemsActions);
    }
  }, // loadProgramBehaviourDataGroups()

  async addProgramBehaviourDataItem({ programId, groupId, item }) {
    const programBehaviourDataItemsDb = new ProgramBehaviourDataItemsDB(programId, groupId);
    const order = chain(this.programBehaviourDataItemCache)
      .get(groupId)
      .map((orderItem) => orderItem.order)
      .max()
      .add(1)
      .value();

    const newItem = await programBehaviourDataItemsDb.create({
      order,
      ...item,
    });
    this.addProgramBehaviourDataItemMutation({ groupId, itemId: newItem.id, item: newItem });
    return newItem;
  }, // addProgramBehaviourDataItem()

  /**
   * Load info about a program group and item from Firebase and return it
   * Loads from cache if already loaded
   */
  async getProgramBehaviourDataItem({ programId, groupId, itemId }) {
    if (!groupId || !itemId) {
      return;
    }

    // Item already cached - just return it
    if (has(this.programBehaviourDataItemCache, [groupId, itemId])) {
      return get(this.programBehaviourDataItemCache, [groupId, itemId]);
    }

    // Check if group was already cached?
    let group;
    if (this.programBehaviourDataGroupCache[groupId]) {
      group = this.programBehaviourDataGroupCache[groupId];
    } else {
      const programBehaviourDataGroupsDb = new ProgramBehaviourDataGroupsDB(programId);
      group = await programBehaviourDataGroupsDb.read(groupId);
      // @todo Refactor this out | START
      if (group === null) {
        group = {
          id: '',
        };
      }
      // @todo Refactor this out | END
      this.addProgramBehaviourDataGroup({ group });
    }

    const programBehaviourDataItemsDb = new ProgramBehaviourDataItemsDB(programId, groupId);
    let item = await programBehaviourDataItemsDb.read(itemId);
    // @todo Refactor this out | START
    if (item === null) {
      item = {
        id: '',
      };
    }
    item.group = group;
    // @todo Refactor this out | END
    this.addProgramBehaviourDataItemMutation({ groupId, itemId, item });

    return item;
  }, // getProgramBehaviourDataItem()

  invalidateBehaviourDataCache() {
    this.clearProgramBehaviourDataCache();
  }, // invalidateBehaviourDataCache()

  logout() {
    this.clearProgramBehaviourDataCache();
  }, // logout()

  setAbcDraft({ abcDraft, draftId }) {
    const draftData = { ...this.abcDraft, [draftId]: abcDraft };

    this.abcDraft = draftData;
    localStorage.setItem('abcDraft', JSON.stringify(draftData));
  }, // setAbcDraft()

  removeCurrentDraft({ draftId }) {
    const { [draftId]: a, ...abcDraft } = this.abcDraft; // eslint-disable-line no-unused-vars

    localStorage.setItem('abcDraft', JSON.stringify(abcDraft));
    this.abcDraft = abcDraft;
  }, // removeAbcDraft()
};
